import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import EditableText from '@sayyesschool/frontend/components/editable-text';
import ImageSection from '@sayyesschool/frontend/components/image-section';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { getImagePath, useUnit } from '@sayyesschool/frontend/features/courses';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Flex, Grid } from '@sayyesschool/frontend/ui-components';

import UnitDescription from '../unit-description';
import UnitLessons from '../unit-lessons';

export default function UnitPage() {
    const navigate = useNavigate();
    const params = useParams();
    const { course, unit, actions } = useUnit({
        courseId: params.courseId,
        unitId: params.unitId
    });

    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const handleUpdateUnit = useCallback(data => {
        return actions.updateUnit(course.id, unit.id, data);
    }, [course, unit, actions]);

    const handleUpdateUnitTitle = useCallback(title => {
        return actions.updateUnit(course.id, unit.id, { title });
    }, [course, unit, actions]);

    const handleUpdateUnitImage = useCallback(image => {
        return actions.updateUnit(course.id, unit.id, { image });
    }, [course, unit, actions]);

    const handleDeleteUnit = useCallback(() => {
        return actions.deleteUnit(course.id, unit.id)
            .then(() => navigate(course.uri));
    }, [course, unit, actions, navigate]);

    const handleCreateLesson = useCallback(data => {
        data.unitId = unit.id;

        return actions.createLesson(course.id, data);
    }, [course, unit, actions]);

    const handleDeleteLesson = useCallback(lesson => {
        return actions.deleteLesson(course.id, lesson.id);
    }, [course, actions]);

    if (!unit) return <LoadingIndicator fullscreen />;

    return (
        <Page className="UnitPage">
            <Page.Header
                title={
                    <EditableText
                        content={unit.title}
                        required
                        onUpdate={handleUpdateUnitTitle}
                    />
                }
                breadcrumbs={[
                    { key: 'course', content: course.title, to: course.uri, title: 'Курс' }
                ]}
                actions={[
                    {
                        key: 'delete',
                        icon: 'delete',
                        title: 'Удалить юнит',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid spacing={2}>
                    <Grid.Item xs={8}>
                        <UnitDescription
                            unit={unit}
                            onUpdate={handleUpdateUnit}
                        />
                    </Grid.Item>

                    <Grid.Item xs={4}>
                        <Flex
                            spacing={2}
                            column
                        >
                            <ImageSection
                                image={unit.image}
                                path={getImagePath(course.id, unit.id)}
                                onChange={handleUpdateUnitImage}
                            />

                            <UnitLessons
                                course={course}
                                unit={unit}
                                onCreate={handleCreateLesson}
                                onDelete={handleDeleteLesson}
                                onReorder={handleUpdateUnit}
                            />
                        </Flex>
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <ConfirmationDialog
                title="Удалить юнит?"
                message={`Юнит "${unit.title}" будет удален без возможности восстановления.`}
                open={isConfirmationDialogOpen}
                onConfirm={handleDeleteUnit}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}